<template>
  <div v-if="tasks.length" ref="rootEl" class="relative">
    <button
      v-for="group in groups"
      :key="group.status"
      type="button"
      data-task-summary
      class="w-full flex items-center gap-1.5 px-1.5 py-0.5 rounded text-[12px] leading-tight truncate transition-colors bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
      @click.stop="store.toggleTaskList(listId)"
    >
      <span class="w-2 h-2 rounded-full flex-shrink-0" :class="group.dotClass"></span>
      <span class="truncate text-gray-600 dark:text-gray-300">{{ group.label }}</span>
    </button>

    <!-- Opens on click, not hover: a panel that appears while the pointer merely
         crosses the row is easy to trigger by accident and impossible on touch.
         The all-day row is short, so it floats above the grid. -->
    <div
      v-if="open"
      data-task-list
      class="absolute left-0 top-full mt-1 z-40 w-64 max-h-72 overflow-y-auto bg-white dark:bg-[#1C2128] rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 py-1"
    >
      <!-- One keyed list rather than two, so Vue can animate a row travelling
           between the sections instead of it vanishing here and appearing there. -->
      <TransitionGroup name="task" tag="div" class="relative">
        <template v-for="row in rows" :key="row.key">
          <p
            v-if="row.type === 'header'"
            data-task-section
            class="px-2.5 pt-2 pb-1 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
          >{{ row.label }} · {{ row.count }}</p>

          <button
            v-else
            type="button"
            data-task-row
            class="w-full flex items-start gap-2 px-2.5 py-1.5 text-left rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            @click.stop="emit('open', row.task)"
          >
            <span
              data-task-checkbox
              role="checkbox"
              :aria-checked="isDone(row.task)"
              :aria-label="`Mark ${row.task.title || 'task'} as ${isDone(row.task) ? 'not done' : 'done'}`"
              class="task-check w-4 h-4 mt-0.5 rounded-full border flex-shrink-0 flex items-center justify-center cursor-pointer"
              :class="isDone(row.task)
                ? 'bg-blue-600 border-blue-600 is-done'
                : 'border-gray-300 dark:border-gray-600 hover:border-blue-500'"
              @click.stop="toggle(row.task)"
            >
              <svg class="task-check-mark w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
              </svg>
            </span>

            <span
              class="relative text-[13px] leading-snug min-w-0 break-words transition-colors duration-300"
              :class="isDone(row.task) ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-200'"
            >
              {{ row.task.title || row.task.name || '(No title)' }}
              <!-- Drawn rather than text-decoration, so it can sweep across -->
              <span class="task-strike" :class="{ 'is-done': isDone(row.task) }"></span>
            </span>
          </button>
        </template>
      </TransitionGroup>

      <p v-if="error" data-task-error class="px-2.5 py-1.5 text-[11px] text-red-600 dark:text-red-400">
        {{ error }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import { useCalendarStore } from 'stores/calendar.js'

// One line per status instead of one chip per task.
//
// The all-day row rendered every task as its own full-width chip, so a day with
// five tasks pushed the row several rows tall and buried the actual all-day
// events. A count reads faster, and the list is one click away.

const props = defineProps({
  tasks: { type: Array, default: () => [] },
})

const emit = defineEmits(['open'])

const store  = useCalendarStore()
const rootEl = ref(null)
const error  = ref(null)

// Identifies this day's list to the store, which only ever keeps one open. The
// instance uid rather than a module counter: a `let` in <script setup> is
// per-instance, so every list would have claimed the same id.
const listId = `task-list-${getCurrentInstance().uid}`

const open = computed(() => store.openTaskListId === listId)

const isDone = (task) => task.status === 'completed'

// Capture phase: the summary button stops the click so it does not reach the
// grid underneath, which would otherwise also stop it reaching this listener.
function onDocumentClick(event) {
  if (open.value && rootEl.value && !rootEl.value.contains(event.target)) store.closeTaskList()
}

function onKeydown(event) {
  if (event.key === 'Escape' && open.value) store.closeTaskList()
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true)
  document.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick, true)
  document.removeEventListener('keydown', onKeydown)
  // Do not leave the store pointing at a list that no longer exists.
  if (open.value) store.closeTaskList()
})

const groups = computed(() => {
  const done    = props.tasks.filter(isDone).length
  const pending = props.tasks.length - done

  return [
    pending > 0 && {
      status: 'pending',
      label: `${pending} ${pending === 1 ? 'task' : 'tasks'} pending`,
      dotClass: 'bg-blue-500',
    },
    done > 0 && {
      status: 'completed',
      label: `${done} ${done === 1 ? 'task' : 'tasks'} done`,
      dotClass: 'bg-green-600',
    },
  ].filter(Boolean)
})

// Headers and tasks share one keyed list so a ticked row animates out of the
// to-do section and down into the done one.
const rows = computed(() => {
  const pending = props.tasks.filter(task => !isDone(task))
  const done    = props.tasks.filter(isDone)
  const out     = []

  if (pending.length) out.push({ type: 'header', key: 'header-pending', label: 'To do', count: pending.length })
  pending.forEach(task => out.push({ type: 'task', key: `task-${task.id}`, task }))

  if (done.length) out.push({ type: 'header', key: 'header-done', label: 'Done', count: done.length })
  done.forEach(task => out.push({ type: 'task', key: `task-${task.id}`, task }))

  return out
})

async function toggle(task) {
  error.value = null
  // The store moves the row between sections before the request goes out, so
  // the animation starts on the click rather than a round trip later. A rejected
  // request puts it back.
  const result = await store.toggleTaskStatus(task)
  if (!result.ok) error.value = result.error
}
</script>

<style scoped>
/* A row travelling between the two sections */
.task-move { transition: transform 380ms cubic-bezier(0.2, 0.8, 0.2, 1); }
.task-enter-active { transition: opacity 220ms ease, transform 220ms ease; }
.task-leave-active { transition: opacity 160ms ease; position: absolute; width: 100%; }
.task-enter-from { opacity: 0; transform: translateY(-4px); }
.task-leave-to { opacity: 0; }

/* Checkbox fills with a small pop */
.task-check {
  transition: background-color 200ms ease, border-color 200ms ease,
              transform 220ms cubic-bezier(0.2, 1.6, 0.4, 1);
}
.task-check.is-done { transform: scale(1.12); }

.task-check-mark {
  opacity: 0;
  transform: scale(0.4);
  transition: opacity 160ms ease 60ms, transform 220ms cubic-bezier(0.2, 1.6, 0.4, 1) 60ms;
}
.task-check.is-done .task-check-mark { opacity: 1; transform: scale(1); }

/* Strike-through swept across rather than switched on */
.task-strike {
  position: absolute;
  left: 0;
  top: 50%;
  height: 1px;
  width: 100%;
  background: currentColor;
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 280ms cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
}
.task-strike.is-done { transform: scaleX(1); }

@media (prefers-reduced-motion: reduce) {
  .task-move,
  .task-enter-active,
  .task-leave-active,
  .task-check,
  .task-check-mark,
  .task-strike { transition-duration: 1ms; }
}
</style>
